import { useState, useRef } from "react";
import { Upload, Link as LinkIcon, X, Loader2, Image as ImageIcon, Film } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { API } from "@/App";
import { authHeaders } from "@/lib/firebase";

/**
 * Host-only widget for managing a trip's gallery. Renders inside TripPage when isHost.
 * Supports file upload (photo or video) AND external URLs (YouTube, Vimeo, Cloudinary, mp4/jpg).
 */
const TripMediaManager = ({ tripId, media, onChange }) => {
  const [uploading, setUploading] = useState(false);
  const [urlInput, setUrlInput] = useState("");
  const [addingUrl, setAddingUrl] = useState(false);
  const fileInputRef = useRef(null);

  const refresh = async () => {
    try {
      const r = await fetch(`${API}/trips/${tripId}/media`);
      if (r.ok) onChange(await r.json());
    } catch (_) { /* noop */ }
  };

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      for (const f of files) {
        const form = new FormData();
        form.append("file", f);
        const headers = await authHeaders();
        const r = await fetch(`${API}/trips/${tripId}/media/upload`, { method: "POST", headers, body: form });
        if (!r.ok) {
          const detail = await r.json().then(d => d.detail).catch(() => null);
          toast.error(detail || `Failed to upload ${f.name}`);
        }
      }
      await refresh();
      toast.success(`Added ${files.length} media item${files.length !== 1 ? "s" : ""}`);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleAddUrl = async () => {
    const url = urlInput.trim();
    if (!url) return;
    setAddingUrl(true);
    try {
      const headers = await authHeaders({ "Content-Type": "application/json" });
      const r = await fetch(`${API}/trips/${tripId}/media/url`, {
        method: "POST", headers, body: JSON.stringify({ url, type: "photo" }),
      });
      if (r.ok) {
        setUrlInput("");
        await refresh();
        toast.success("URL added");
      } else {
        const detail = await r.json().then(d => d.detail).catch(() => null);
        toast.error(detail || "Failed to add URL");
      }
    } finally { setAddingUrl(false); }
  };

  const handleDelete = async (mediaId) => {
    if (!window.confirm("Remove this from the gallery?")) return;
    try {
      const headers = await authHeaders();
      const r = await fetch(`${API}/trips/${tripId}/media/${mediaId}`, { method: "DELETE", headers });
      if (r.ok) { await refresh(); toast.success("Removed"); }
      else toast.error("Failed to remove");
    } catch (_) { toast.error("Remove error"); }
  };

  return (
    <div className="rounded-2xl border border-border p-5 space-y-4" data-testid="trip-media-manager">
      <div>
        <h3 className="font-display text-lg">Gallery</h3>
        <p className="text-xs text-muted-foreground">Add photos and videos. The hero shows the first 5; the rest appear in the lightbox.</p>
      </div>

      {/* Upload */}
      <div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,video/*"
          multiple
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
        />
        <Button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="w-full bg-foreground text-background hover:bg-foreground/90 gap-2"
          data-testid="trip-media-upload-btn"
        >
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          Upload photo or video
        </Button>
      </div>

      {/* URL input */}
      <div>
        <div className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-1.5">Or paste a URL</div>
        <div className="flex gap-2">
          <Input
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAddUrl(); } }}
            placeholder="YouTube, Vimeo, Cloudinary or direct .mp4/.jpg URL"
            data-testid="trip-media-url-input"
          />
          <Button type="button" variant="outline" onClick={handleAddUrl} disabled={addingUrl || !urlInput.trim()}>
            {addingUrl ? <Loader2 className="w-4 h-4 animate-spin" /> : <LinkIcon className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {/* Current gallery list */}
      {media.length > 0 && (
        <div>
          <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-2">{media.length} item{media.length !== 1 ? "s" : ""}</p>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {media.map(m => (
              <div key={m.media_id} className="relative aspect-square rounded-lg overflow-hidden bg-muted group">
                {m.type === "video" ? (
                  <div className="absolute inset-0 flex items-center justify-center bg-foreground/10 text-foreground">
                    <Film className="w-6 h-6" />
                  </div>
                ) : (
                  <img src={m.url} alt="" className="w-full h-full object-cover" />
                )}
                <button
                  type="button"
                  onClick={() => handleDelete(m.media_id)}
                  className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/70 text-white opacity-0 group-hover:opacity-100 flex items-center justify-center"
                  aria-label="Remove"
                  data-testid={`trip-media-delete-${m.media_id}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {media.length === 0 && !uploading && (
        <div className="rounded-xl border border-dashed border-border p-4 text-center text-sm text-muted-foreground inline-flex items-center justify-center gap-2 w-full">
          <ImageIcon className="w-4 h-4" /> No media yet — your trip will use the cover image.
        </div>
      )}
    </div>
  );
};

export default TripMediaManager;
